import { IconButton } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import ExportIcon from '@material-ui/icons/CloudDownload'
import isNumber from 'lodash/isNumber'
import upperFirst from 'lodash/upperFirst'
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import 'temporal-polyfill/global'

import { DomainPermission_VerbEnum, DomainPermission_DomainEnum } from 'src/api/gen'
import { Vehicle_TypeEnum, Provider, TripsAggregations, ZoneName, TripCountInStreetSegment } from 'src/api/gen/api'
import { ITripListFilters } from 'src/api/types/trips'
import { getEnumEntries, getProviderEntries } from 'src/commons/filters/utils'
import { lexicographicSort } from 'src/commons/helpers'
import { withFormattedCount, enumsTranslationKeys } from 'src/commons/i18nEnums'
import { userHasPermission, userHasAtLeastOnePermission } from 'src/commons/permissions'
import { IDateTimeRange } from 'src/components/DatePickers'
import { useDayOfWeekEntry } from 'src/components/DayOfWeek'
import { Slider } from 'src/components/Slider'
import { useAgencySettings, useAgencySettingsDistanceUnit, useAgencySettingsSpeedUnit } from 'src/layout/AgencySettings'
import { useMapLayer } from 'src/layout/MapLayerWrapper'
import CollapsibleFilter from 'src/muiv5/components/commons/SidePanel/Filters/BaseFilter/CollapsibleFilter'
import CheckboxListFilter from 'src/muiv5/components/commons/SidePanel/Filters/CheckboxListFilter/CheckboxListFilter'
import SliderFilter from 'src/muiv5/components/commons/SidePanel/Filters/Slider/SliderFilter'
import TimeRangeFilterPro from 'src/muiv5/components/commons/SidePanel/Filters/TimeRangeFilter/TimeRangeFilterPro'
import { SidePanel } from 'src/muiv5/components/commons/SidePanel/SidePanel'
import SETTINGS from 'src/settings'

import { formatDuration } from './common'
import {
  DEFAULT_MIN_DURATION,
  DEFAULT_MAX_DURATION,
  DEFAULT_MIN_DISTANCE,
  DEFAULT_MAX_DISTANCE,
  DEFAULT_MIN_TRIPS_PER_STREET,
  DEFAULT_MAX_TRIPS_PER_STREET,
  DEFAULT_MIN_SPEED,
  DEFAULT_MAX_SPEED,
} from './constants'
import { fetchExportTripHeatmap, useFetchZoneNames } from './requests'

const useStyles = makeStyles(() => ({
  exportIcon: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
  centerCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}))

export type FilterPanelValue = string | number | string[] | Vehicle_TypeEnum[] | IDateTimeRange | number[]

export interface IProps {
  apiOptions?: Record<string, unknown>
  defaultFilters: ITripListFilters
  initialFilters: ITripListFilters
  /*
    this is very ugly, we pass down these attributes in order to change filters dynamically.
    we should have split the whole FilterPanel into a Table and Map one, but the issue is that
    it's not very well factorized. It would have lead to A LOT of duplicated code.
  */
  isHeatmapTripsSelected: boolean
  onChangeMapLayer: (key: string) => void
  providers: Provider[]
  tripCountsInStreetSegments: TripCountInStreetSegment
  tripsCount: number
  tripsFacets: TripsAggregations
}

const TripFilterPanel = forwardRef(function (props: IProps, ref) {
  const { t } = useTranslation()
  const classes = useStyles()

  const [filters, setFilters] = useState(props.initialFilters)
  useImperativeHandle(ref, () => filters, [filters])

  const onFilterChange = function (key: string, newValue: FilterPanelValue) {
    setFilters({ ...filters, [key]: newValue })
  }

  const dayOfWeekEntry = useDayOfWeekEntry()
  const fetchZoneNames = useFetchZoneNames()

  const [zones, setZones] = useState<ZoneName[]>([])

  const { allowed_vehicle_types } = useAgencySettings()
  const { value: distanceUnit, symbol: distanceUnitSymbol } = useAgencySettingsDistanceUnit()
  const { value: speedUnit, symbol: speedUnitSymbol } = useAgencySettingsSpeedUnit()
  const mapLayers = useMapLayer()

  const [durationMin, setDurationMin] = useState<number>(DEFAULT_MIN_DURATION)
  const [durationMax, setDurationMax] = useState<number>(DEFAULT_MAX_DURATION)
  const [distanceMin, setDistanceMin] = useState<number>(DEFAULT_MIN_DISTANCE[distanceUnit])
  const [distanceMax, setDistanceMax] = useState<number>(DEFAULT_MAX_DISTANCE[distanceUnit])
  const [speedMin, setSpeedMin] = useState<number>(DEFAULT_MIN_SPEED[speedUnit])
  const [speedMax, setSpeedMax] = useState<number>(DEFAULT_MAX_SPEED[speedUnit])

  const tripsPerStreetMin = props.tripCountsInStreetSegments.trips_per_street_min || DEFAULT_MIN_TRIPS_PER_STREET
  const tripsPerStreetMax = props.tripCountsInStreetSegments.trips_per_street_max || DEFAULT_MAX_TRIPS_PER_STREET

  useEffect(() => {
    if (props.tripsFacets.duration_min !== undefined) {
      setDurationMin(Math.min(props.tripsFacets.duration_min, durationMin))
    }
    if (props.tripsFacets.duration_max !== undefined) {
      setDurationMax(Math.max(props.tripsFacets.duration_max, durationMax))
    }
    if (props.tripsFacets.distance_min !== undefined) {
      setDistanceMin(Math.min(props.tripsFacets.distance_min, distanceMin))
    }
    if (props.tripsFacets.distance_max !== undefined) {
      setDistanceMax(Math.max(props.tripsFacets.distance_max, distanceMax))
    }
    if (props.tripsFacets.speed_min !== undefined) {
      setSpeedMin(Math.min(props.tripsFacets.speed_min, speedMin))
    }
    if (props.tripsFacets.speed_max !== undefined) {
      setSpeedMax(Math.max(props.tripsFacets.speed_max, speedMax))
    }
  }, [props.tripsFacets])

  useEffect(() => {
    async function fetchZones() {
      if (filters.layer) {
        const zonesInResponse = await fetchZoneNames(filters.layer)
        const sortedZones = lexicographicSort(
          zonesInResponse.map((zone: ZoneName) => ({ slug: zone.slug, name: zone.name })),
          'name'
        )
        setZones(sortedZones)
      }
    }
    fetchZones()
  }, [filters.layer])

  function onDistanceChange(newDistance: number | [number, number]) {
    if (isNumber(newDistance)) {
      return
    }
    onFilterChange('distance', newDistance)
  }

  function onSpeedChange(newSpeed: number | [number, number]) {
    if (isNumber(newSpeed)) {
      return
    }
    onFilterChange('speed', newSpeed)
  }

  function onDurationChange(newDuration: number | [number, number]) {
    if (isNumber(newDuration)) {
      return
    }
    onFilterChange('duration', newDuration)
  }

  function onTripsPerStreetChange(newTripsPerStreet: number | [number, number]) {
    if (isNumber(newTripsPerStreet)) {
      return
    }
    onFilterChange('tripsPerStreet', newTripsPerStreet)
  }

  const vehicleTypeEntries = getEnumEntries(
    allowed_vehicle_types ?? [],
    enumsTranslationKeys.DeviceType,
    props.tripsFacets.vehicle_type
  )

  let providerEntries = null
  if (userHasPermission(['read', 'trip'])) {
    providerEntries = getProviderEntries(props.providers, props.tripsFacets.provider_id, true)
  }

  function onStartFromChanged(value?: Temporal.ZonedDateTime) {
    onFilterChange('dateStartRange', { from: value, to: filters.dateStartRange.to })
  }

  function onStartToChanged(value?: Temporal.ZonedDateTime) {
    onFilterChange('dateStartRange', { from: filters.dateStartRange.from, to: value })
  }

  function onStartBothChanged(valueFrom?: Temporal.ZonedDateTime, valueTo?: Temporal.ZonedDateTime) {
    onFilterChange('dateStartRange', { from: valueFrom, to: valueTo })
  }

  function onEndFromChanged(value?: Temporal.ZonedDateTime) {
    onFilterChange('dateEndRange', { from: value, to: filters.dateEndRange.to })
  }

  function onEndToChanged(value?: Temporal.ZonedDateTime) {
    onFilterChange('dateEndRange', { from: filters.dateEndRange.from, to: value })
  }

  function onEndBothChanged(valueFrom?: Temporal.ZonedDateTime, valueTo?: Temporal.ZonedDateTime) {
    onFilterChange('dateEndRange', { from: valueFrom, to: valueTo })
  }

  return (
    <SidePanel
      headerProps={{
        title: t('filtersPanel.title.default'),
        information: t('tripsWithCount', withFormattedCount(props.tripsCount)),
        actions: props.isHeatmapTripsSelected &&
          userHasAtLeastOnePermission([
            [DomainPermission_VerbEnum.summarize, DomainPermission_DomainEnum.trip],
            [DomainPermission_VerbEnum.analyze, DomainPermission_DomainEnum.street_segment],
          ]) && (
            <div className={classes.centerCell}>
              <IconButton
                disabled={!!props.tripCountsInStreetSegments.error_message}
                onClick={() => fetchExportTripHeatmap(filters, props.apiOptions)}
              >
                <ExportIcon className={classes.exportIcon} />
              </IconButton>
            </div>
          ),
        onReset: () => setFilters(props.defaultFilters),
      }}
    >
      <CheckboxListFilter
        filterId={'vehicle_type'}
        items={vehicleTypeEntries}
        values={filters['vehicle_type']}
        title={upperFirst(t('filtersPanel.vehicleType'))}
        onFilterChange={onFilterChange}
        displayCheckbox
      />
      {providerEntries && (
        <CheckboxListFilter
          filterId={'provider'}
          items={providerEntries}
          values={filters['provider']}
          title={upperFirst(t('filtersPanel.provider'))}
          onFilterChange={onFilterChange}
          displayCheckbox
          defaultExpanded
        />
      )}
      <TimeRangeFilterPro
        filterId={'dateStartRange'}
        format="L LT"
        values={{
          from: filters.dateStartRange.from,
          to: filters.dateStartRange.to,
        }}
        title={upperFirst(t('trips.startTime'))}
        onFromChanged={onStartFromChanged}
        onToChanged={onStartToChanged}
        onBothChanged={onStartBothChanged}
        missingValue={!(filters.dateStartRange.from && filters.dateStartRange.to)}
        disableFuture
        normalize
      />
      <TimeRangeFilterPro
        filterId={'dateEndRange'}
        format="L LT"
        values={{
          from: filters.dateEndRange.from,
          to: filters.dateEndRange.to,
        }}
        title={upperFirst(t('trips.endTime'))}
        onFromChanged={onEndFromChanged}
        onToChanged={onEndToChanged}
        onBothChanged={onEndBothChanged}
        missingValue={!(filters.dateEndRange.from && filters.dateEndRange.to)}
        disableFuture
        normalize
      />

      <CheckboxListFilter
        filterId={'dayOfWeek'}
        items={dayOfWeekEntry}
        values={filters.dayOfWeek}
        title={upperFirst(t('trips.dayOfWeek'))}
        onFilterChange={onFilterChange}
        displayCheckbox
      />

      <CollapsibleFilter
        filterId={'duration'}
        title={upperFirst(t('trips.duration'))}
        subtitle={t('filters.date.rangeDurationSubtitle', {
          start: formatDuration(Math.max(filters.duration[0], durationMin)),
          end: formatDuration(Math.min(filters.duration[1], durationMax)),
        })}
      >
        <SliderFilter
          value={[Math.max(filters.duration[0], durationMin), Math.min(filters.duration[1], durationMax)]}
          min={durationMin}
          max={durationMax}
          onChange={onDurationChange}
          unit={t('min')}
          formatValue={(durationInMinutes: number) => formatDuration(durationInMinutes)}
          disabled={!(durationMin || durationMax)}
          disableFuture={true}
          clearable={true}
        />
      </CollapsibleFilter>

      {filters.speed !== undefined && (
        <CollapsibleFilter
          filterId={'speed'}
          title={upperFirst(t('trips.speed'))}
          subtitle={t('filters.date.rangeSpeedSubtitle', { start: speedMin, end: speedMax })}
        >
          <SliderFilter
            value={[Math.max(filters.speed[0], speedMin), Math.min(filters.speed[1], speedMax)]}
            min={speedMin}
            max={speedMax}
            onChange={onSpeedChange}
            unit={t(speedUnitSymbol)}
            formatValue={(speed: number) => `${speed.toString()} ${t(speedUnitSymbol)}`}
            disabled={!(speedMin || speedMax)}
            disableFuture={true}
            clearable={true}
          />
        </CollapsibleFilter>
      )}

      {props.isHeatmapTripsSelected && (
        <CollapsibleFilter filterId={'perStreet'} title={upperFirst(t('trips.perStreet'))}>
          <Slider
            value={[
              Math.max(filters.tripsPerStreet[0], tripsPerStreetMin),
              Math.min(filters.tripsPerStreet[1], tripsPerStreetMax),
            ]}
            min={tripsPerStreetMin}
            max={tripsPerStreetMax}
            onChange={onTripsPerStreetChange}
            disabled={
              tripsPerStreetMin === DEFAULT_MIN_TRIPS_PER_STREET && tripsPerStreetMax === DEFAULT_MAX_TRIPS_PER_STREET
            }
            disableFuture={true}
            clearable={true}
          />
        </CollapsibleFilter>
      )}
      {filters.distance !== undefined && (
        <CollapsibleFilter filterId={'distance'} title={upperFirst(t('trips.distance'))}>
          <Slider
            value={[Math.max(filters.distance[0], distanceMin), Math.min(filters.distance[1], distanceMax)]}
            min={distanceMin}
            max={distanceMax}
            step={SETTINGS.app.filter_trips_step}
            onChange={onDistanceChange}
            unit={t(distanceUnitSymbol)}
            formatValue={(distance: number) => `${distance.toString()} ${t(distanceUnitSymbol)}`}
            disableFuture={true}
            disabled={!(distanceMin || distanceMax)}
            clearable={true}
          />
        </CollapsibleFilter>
      )}
      <CheckboxListFilter
        filterId={'layer'}
        items={mapLayers}
        values={filters.layer ? [filters.layer] : undefined}
        title={upperFirst(t('map layer'))}
        unique
        onFilterChange={(key: string, newValue: string[]) => onFilterChange(key, newValue[newValue.length - 1])}
        displayCheckbox
      />
      {filters.layer && zones && (
        <>
          <CheckboxListFilter
            filterId={'origins'}
            title={upperFirst(t('trips.origin'))}
            items={zones.map((zone) => ({ id: zone.slug, label: zone.name }))}
            values={filters.origins}
            onFilterChange={onFilterChange}
            displayCheckbox
            autocomplete
            nullable
          />
          <CheckboxListFilter
            filterId={'destinations'}
            title={upperFirst(t('trips.destination'))}
            items={zones.map((zone) => ({ id: zone.slug, label: zone.name }))}
            values={filters.destinations}
            onFilterChange={onFilterChange}
            displayCheckbox
            autocomplete
            nullable
          />
        </>
      )}
    </SidePanel>
  )
})

export default TripFilterPanel
